import React, { useRef } from "react";
import { Box, BoxProps } from "@chakra-ui/react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const MotionBox = motion(Box);

// Tilts its children in 3D toward the pointer, with a spring so it settles softly.
export function Tilt3D({ children, maxTiltDegrees = 8, ...props }: BoxProps & { maxTiltDegrees?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const pointerX = useMotionValue(0.5);
  const pointerY = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(pointerY, [0, 1], [maxTiltDegrees, -maxTiltDegrees]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(pointerX, [0, 1], [-maxTiltDegrees, maxTiltDegrees]), { stiffness: 120, damping: 18 });

  return (
    <Box ref={ref} style={{ perspective: "1200px" }} {...props}>
      <MotionBox
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        onPointerMove={(event: React.PointerEvent) => {
          const rect = ref.current?.getBoundingClientRect();
          if (!rect) return;
          pointerX.set((event.clientX - rect.left) / rect.width);
          pointerY.set((event.clientY - rect.top) / rect.height);
        }}
        onPointerLeave={() => {
          pointerX.set(0.5);
          pointerY.set(0.5);
        }}
      >
        {children}
      </MotionBox>
    </Box>
  );
}

// Moves its children a fraction of the pointer's distance from the viewport centre — cheap depth for foliage layers.
export function PointerParallax({ children, depth = 20, ...props }: BoxProps & { depth?: number }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 60, damping: 20 });
  const springY = useSpring(y, { stiffness: 60, damping: 20 });

  React.useEffect(() => {
    const onMove = (event: PointerEvent) => {
      x.set(((event.clientX / window.innerWidth) - 0.5) * depth);
      y.set(((event.clientY / window.innerHeight) - 0.5) * depth);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [x, y, depth]);

  return (
    <MotionBox style={{ x: springX, y: springY }} {...props}>
      {children}
    </MotionBox>
  );
}
